/**
 * useKeyboardShortcuts Hook
 *
 * Global keyboard shortcut management system.
 * Listens for keyboard events and triggers registered callbacks.
 *
 * Features:
 * - Prevents conflicts with input fields (typing)
 * - Supports modifier keys (Ctrl, Alt, Shift, Meta)
 * - Prevents default browser behavior when needed
 * - Accessible and non-intrusive
 */

import { useEffect } from 'react'

export interface KeyboardShortcutHandler {
  /** The key to listen for (e.g., 'F9', ' ' for space, 'Escape') */
  key: string

  /** Whether Ctrl/Cmd should be pressed */
  ctrlKey?: boolean

  /** Whether Alt/Option should be pressed */
  altKey?: boolean

  /** Whether Shift should be pressed */
  shiftKey?: boolean

  /** Whether Meta/Command should be pressed (Mac) */
  metaKey?: boolean

  /** Callback to execute when shortcut is triggered */
  callback: (event: KeyboardEvent) => void

  /** Whether to prevent default browser behavior */
  preventDefault?: boolean

  /** Optional description for debugging */
  description?: string
}

/**
 * Check if the target element is an input field where typing should not be interrupted
 */
function isInputElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) {
    return false
  }

  const tagName = target.tagName.toLowerCase()
  const isContentEditable = target.contentEditable === 'true'

  return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || isContentEditable
}

/**
 * Check if a keyboard event matches a shortcut definition
 */
function matchesShortcut(event: KeyboardEvent, shortcut: KeyboardShortcutHandler): boolean {
  // Check key match (case-insensitive for letters, exact for special keys)
  const keyMatches =
    event.key.toLowerCase() === shortcut.key.toLowerCase() || event.key === shortcut.key

  if (!keyMatches) return false

  // Check modifier keys
  const ctrlMatches = shortcut.ctrlKey === undefined || event.ctrlKey === shortcut.ctrlKey
  const altMatches = shortcut.altKey === undefined || event.altKey === shortcut.altKey
  const shiftMatches = shortcut.shiftKey === undefined || event.shiftKey === shortcut.shiftKey
  const metaMatches = shortcut.metaKey === undefined || event.metaKey === shortcut.metaKey

  return ctrlMatches && altMatches && shiftMatches && metaMatches
}

/**
 * Register global keyboard shortcuts
 *
 * @param shortcuts - Array of keyboard shortcut handlers
 * @param enabled - Whether shortcuts are currently enabled (default: true)
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts([
 *   {
 *     key: 'F9',
 *     callback: () => togglePlayback(),
 *     preventDefault: true,
 *     description: 'Toggle TTS playback',
 *   },
 *   {
 *     key: 'Escape',
 *     callback: () => stopReading(),
 *     description: 'Stop TTS',
 *   },
 * ]);
 * ```
 */
export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcutHandler[],
  enabled: boolean = true,
) {
  useEffect(() => {
    if (!enabled || shortcuts.length === 0) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't interfere with typing in input fields
      if (isInputElement(event.target)) {
        return
      }

      // Check each shortcut
      for (const shortcut of shortcuts) {
        if (matchesShortcut(event, shortcut)) {
          console.log('[KeyboardShortcuts] Triggered:', shortcut.description || shortcut.key)

          if (shortcut.preventDefault) {
            event.preventDefault()
          }

          shortcut.callback(event)
          break // Only trigger first matching shortcut
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [shortcuts, enabled])
}
